import axios from 'axios';
import { getToken } from 'firebase/app-check';
import { v4 as uuidv4 } from 'uuid';
import { AppCheck } from 'firebase/app-check';
import { TransactionalMessageId } from '@/types/CustomerIO';
import { OEM_KEY_TO_PROJECT_MAP, ENV } from '@/constants';
import { Auth } from 'firebase/auth';
import { EmailData } from '../types/CustomerIO';

type Env = typeof ENV[keyof typeof ENV];

const FUNCTION_HOST_PATHS = {
	[ENV.LOCAL]: 'http://localhost:5000/airdealer-67669/us-central1',
	// return 'https://us-central1-airdealer-67669.cloudfunctions.net';
	// return 'https://ekhodealer-tony.ngrok.app/airdealer-67669/us-central1';
	[ENV.DEVELOPMENT]: 'http://localhost:5000/airdealer-67669/us-central1',
	[ENV.STAGING]: 'https://us-central1-ed-staging.cloudfunctions.net',
	[ENV.PRODUCTION]: 'https://us-central1-ad-backend-prod.cloudfunctions.net',
};

export function setFunctionHost(env: Env) {
	return FUNCTION_HOST_PATHS[env] || FUNCTION_HOST_PATHS[ENV.LOCAL];
}

// Helper function for getting the AppCheck token
async function getAppCheckToken(appCheck: AppCheck): Promise<{ token: string }> {
	try {
		const appCheckToken = await getToken(appCheck, false);
		return appCheckToken;
	} catch (error) {
		throw new Error('Failed to get AppCheck token: ' + error);
	}
}

// function that returns the correct headers for a request
function getRequestHeaders(appCheckToken: { token: string }, authToken?: string): Record<string, string> {
	// Add app check token to request headers
	const functionHeaders: Record<string, string> = { 'X-Firebase-AppCheck': appCheckToken.token };

	if (authToken) {
		functionHeaders.Authorization = `Bearer ${authToken}`;
	}

	// Generate a 32-character hexadecimal trace ID
	const traceId = uuidv4().replace(/-/g, '');
	// Generate a 16-character hexadecimal span ID
	const spanId = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
		.toString(16)
		.padStart(16, '0');
	// Set the sampled flag to '01' to indicate the trace should be sampled
	const traceFlags = '01';

	// Construct the traceparent header
	const traceparent = `00-${traceId}-${spanId}-${traceFlags}`;

	functionHeaders.traceparent = traceparent;
	return functionHeaders;
}

export async function sendTransactionalEmail(
	messageData: EmailData,  
	oemKey: string,
	appCheck: AppCheck,
	functionHost: string, 
	messageId?: TransactionalMessageId,
): Promise<any> {
	console.log('messageData', messageData);
	console.log('oemKey', oemKey);
	console.log('functionHost', functionHost);
	console.log('messageId', messageId);
	const body = {
		oemKey,
		messageData,
		messageId,
	};
	try {
		const url = `${functionHost}/send_transactional_email_v2/sendTransactionalEmail`;
		let headers = {};
		
		try {
			const appCheckToken = await getAppCheckToken(appCheck);
			headers = getRequestHeaders(appCheckToken);
		} catch (appCheckError) {
			console.error('AppCheck error:', appCheckError);
			// Continue without AppCheck token
		}
		
		const response = await axios.post(url, body, { headers });
		console.log('sendTransactionalEmail response', response);
		if (response.status !== 200) {
			throw new Error(`Error sending transactional email. Response: ${response}`);
		}
		return response;
	} catch (e) {
		throw new Error(`Error sending transactional email: ${e}`);
	}
} 

//   - auth: firebase auth object
//   - logName: string containing log name. Naming convention: separate words with hyphens
//   - logMessage: string containing log message
//   - severity: severity of the log -> enum: INFO, WARNING, ERROR, CRITICAL
//   - labels: object containing custom labels -> userId, oemKey, etc)
// NOTE: always call this function without await. We don't want to block on logging.
export interface CreateLogParams {
	logName: string;
	logMessage: string;
	severity: "INFO" | "WARNING" | "ERROR" | "CRITICAL";
	labels: Record<string, string>;
	oemKey: string;
	functionHost: string;
	auth: Auth | null;
	appCheck: AppCheck;
}

export async function createLog({ logName, logMessage, severity, labels, oemKey, functionHost, auth, appCheck }: CreateLogParams) {
	//   - projectId: string containing projectId
	const projectId = OEM_KEY_TO_PROJECT_MAP[oemKey as keyof typeof OEM_KEY_TO_PROJECT_MAP];
	if (!projectId) {
		throw new Error(`Invalid environment: ${oemKey}`);
	}
	//   - siteName: string containing client app site name -> // i.e.) 'e2etestoem' (for buyer portal) || 'e2etestoem-checkout' (for checkout)
	const siteName = window.location.hostname.replace('.ekhodealer.com', '');
	const body = {
		oemKey: oemKey,
		logName,
		logMessage,
		siteName,
		projectId,
		severity,
		labels: { ...labels, oemKey: oemKey },
	};
	try {
		const appCheckToken = await getAppCheckToken(appCheck);
		// if calling from an authenticated environment
		if (auth && auth.currentUser) {
			const token = await auth.currentUser.getIdToken();
			const url = `${functionHost}/log_oncreate_v2`;
			await axios.post(url, body, {
				headers: getRequestHeaders(appCheckToken, token),
			});
		} else {
			const url = `${functionHost}/log_oncreate_v2/unauthenticated`;
			await axios.post(url, body, {
				headers: getRequestHeaders(appCheckToken),
			});
		}
	} catch (e) {
		throw new Error(`Error creating log: ${e}`);
	}
}